import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import React, { ReactElement, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import Filter from "components/Filter";
import OfficeProps from "components/Office/OfficeProps";
import ProfileProps from "components/Profile/ProfileProps";
import Section from "components/Section";
import { APPEND_DATA_BEGIN } from "store/actionTypes";
import { Wrapper } from "style/components/Page";
import { LocationChangeState } from "types/state/LocationChangeState";
import ucFirst from "utils/ucFirst";

import {
    Container,
    SearchResults,
    SearchResult,
    LoadMore,
    NoResults,
    SearchForm,
    SearchFilters,
    SearchProfile,
} from "./SearchResultsPage.styled";
import SearchResultPageProps, {
    SearchResultsProps,
} from "./SearchResultsPageProps";

const SearchResultsPage = ({
    paginatedList,
    theme = "lightgray",
    contentTheme = "black",
    actionTheme = "black",
    l18n,
}: SearchResultPageProps): ReactElement => {
    const [searchTerm, setSearchTerm] = React.useState<string | undefined>();
    const dispatch = useDispatch();
    const sendDataToGTM = useGTMDispatch();
    const location = useLocation<LocationChangeState>();

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const queryTerm = query.get("q");

        setSearchTerm(queryTerm || undefined);

        if (queryTerm) {
            sendDataToGTM({
                event: "search",
                search_text: queryTerm,
            });
        }
    }, [location, sendDataToGTM]);

    const handleLoadMore = () => {
        dispatch({
            type: APPEND_DATA_BEGIN,
            payload: {
                path: location.pathname,
                search: paginatedList.currentFilterQuery || "",
                page: paginatedList.currentPage + 1,
                pageSize: paginatedList.pageSize || 50,
            },
        });
    };

    const showLoadMore = (list: SearchResultsProps | undefined): boolean => {
        if (list?.items === undefined || list.totalItems <= list.items.length) {
            return false;
        }
        const lastPage = Math.ceil(list.totalItems / list.pageSize);
        return list.currentPage < lastPage;
    };

    return (
        <Wrapper id="maincontent">
            <ThemeProvider
                theme={{
                    theme: theme,
                    contentTheme: contentTheme,
                    actionTheme: actionTheme,
                }}
            >
                <Section theme={theme} alignItems="center">
                    <Container>
                        <SearchForm
                            searchPageUrl={location.pathname}
                            searchLabel={l18n.searchLabel}
                            searchPlaceholder={l18n.searchPlaceholder}
                            predefinedValue={searchTerm}
                            debounce={true}
                            id="searchfield"
                        />
                        {paginatedList?.filters?.length > 0 && (
                            <SearchFilters>
                                {paginatedList.filters.map((filter, index) => (
                                    <Filter
                                        selected={filter.selected}
                                        url={filter.url}
                                        key={`SearchFilter${index}`}
                                        disabled={
                                            !filter.count || filter.count === 0
                                        }
                                    >
                                        <span>
                                            {filter.linkText} (
                                            {filter.count || 0})
                                        </span>
                                    </Filter>
                                ))}
                            </SearchFilters>
                        )}
                        <SearchResults id="results">
                            {paginatedList?.totalItems > 0
                                ? paginatedList?.items?.map((item, index) => {
                                      if (
                                          item.content?.component === "Office"
                                      ) {
                                          const content =
                                              item.content as OfficeProps;

                                          const heading = [
                                              ucFirst(content.city),
                                              content.addressRow1,
                                          ]
                                              .filter(Boolean)
                                              .join(", ");

                                          const introduction = [
                                              content.addressRow1,
                                              [
                                                  content.postalCode,
                                                  ucFirst(content.city),
                                              ]
                                                  .filter(Boolean)
                                                  .join(" "),
                                          ]
                                              .filter(Boolean)
                                              .join(", ");

                                          return (
                                              <SearchResult
                                                  key={`SearchResult${index}`}
                                                  label={item.hitType}
                                                  heading={heading}
                                                  text={introduction}
                                                  url={item.url}
                                                  contentTheme={contentTheme}
                                                  gtmClickType="search"
                                              />
                                          );
                                      }

                                      if (
                                          item.content?.component === "Profile"
                                      ) {
                                          const content =
                                              item.content as ProfileProps;
                                          return (
                                              <SearchProfile
                                                  fullName={content.fullName}
                                                  initials={content.initials}
                                                  imageUrl={content.imageUrl}
                                                  role={content.role}
                                                  businessArea={
                                                      content.businessArea
                                                  }
                                                  primaryOffice={
                                                      content.primaryOffice
                                                  }
                                                  secondaryOffices={
                                                      content.secondaryOffices
                                                  }
                                                  key={`SearchResult${index}`}
                                                  showOffices={true}
                                                  size="medium"
                                                  gtmClickType="search"
                                                  url={item.url}
                                              />
                                          );
                                      }

                                      return (
                                          <SearchResult
                                              key={`SearchResult${index}`}
                                              label={item.hitType}
                                              heading={item.heading}
                                              text={item.introduction}
                                              url={item.url}
                                              contentTheme={contentTheme}
                                              gtmClickType="search"
                                          />
                                      );
                                  })
                                : paginatedList?.currentFilterQuery &&
                                  paginatedList.currentFilterQuery !== "" && (
                                      <NoResults>
                                          {l18n?.noResults || "No results"}
                                      </NoResults>
                                  )}
                        </SearchResults>
                        {showLoadMore(paginatedList) && (
                            <LoadMore
                                variant="outline"
                                actionTheme="black"
                                size="large"
                                onClick={handleLoadMore}
                            >
                                {l18n?.loadMore || "Load more"}
                            </LoadMore>
                        )}
                    </Container>
                </Section>
            </ThemeProvider>
        </Wrapper>
    );
};

export default React.memo(SearchResultsPage);
